/**
 * Comment thread edits on `.zwdoc` text (#251): replies, resolve, edits and
 * deletes rewrite `header.comments`; deleting a comment also removes its
 * marks / anchors. Adding a comment is a ProseMirror transaction
 * (`ProseEditor` `addComment`), which starts from `newCommentThread`.
 */

import { isNothing, toMaybe } from "../lib/maybe";

import { authorFieldsForWrite } from "./commentAuthor";
import {
  readCommentStore,
  writeCommentStore,
  type CommentMessage,
  type CommentThread,
} from "./commentStore";
import { parseZwDocCached, serializeZwDoc } from "../lib/zwdoc/format";
import { withoutComment, zwDocCommentRanges } from "../lib/zwdoc/comments";

export function newMessageId(): string {
  return `m_${Math.random().toString(36).slice(2, 8)}`;
}

function localMessage(
  body: string,
  createdAt = new Date().toISOString(),
): CommentMessage {
  return {
    id: newMessageId(),
    body,
    createdAt,
    ...authorFieldsForWrite(),
  };
}

/** A new thread with one message by the current author (may be empty). */
export function newCommentThread(body: string): CommentThread {
  const now = new Date().toISOString();
  return {
    createdAt: now,
    resolved: false,
    messages: [localMessage(body, now)],
  };
}

export function setCommentResolved(
  doc: string,
  id: string,
  resolved: boolean,
): string {
  const store = readCommentStore(doc);
  const thread = toMaybe(store[id]);
  if (isNothing(thread)) return doc;
  return writeCommentStore(doc, {
    ...store,
    [id]: { ...thread.value, resolved },
  });
}

export function appendReply(
  doc: string,
  threadId: string,
  body: string,
): string {
  const trimmed = body.trim();
  if (trimmed.length === 0) return doc;
  const store = readCommentStore(doc);
  const thread = toMaybe(store[threadId]);
  if (isNothing(thread)) return doc;
  return writeCommentStore(doc, {
    ...store,
    [threadId]: {
      ...thread.value,
      resolved: false,
      messages: [...thread.value.messages, localMessage(trimmed)],
    },
  });
}

export function updateMessage(
  doc: string,
  threadId: string,
  messageId: string,
  body: string,
): string {
  const store = readCommentStore(doc);
  const thread = toMaybe(store[threadId]);
  if (isNothing(thread)) return doc;
  const index = thread.value.messages.findIndex((m) => m.id === messageId);
  if (index < 0) return doc;
  const messages = thread.value.messages.slice();
  const existing = toMaybe(messages[index]);
  if (isNothing(existing)) return doc;
  messages[index] = { ...existing.value, body };
  return writeCommentStore(doc, {
    ...store,
    [threadId]: { ...thread.value, messages },
  });
}

export function deleteMessage(
  doc: string,
  threadId: string,
  messageId: string,
): string {
  const store = readCommentStore(doc);
  const thread = toMaybe(store[threadId]);
  if (isNothing(thread)) return doc;
  const messages = thread.value.messages.filter((m) => m.id !== messageId);
  if (messages.length === 0) return deleteComment(doc, threadId);
  return writeCommentStore(doc, {
    ...store,
    [threadId]: { ...thread.value, messages },
  });
}

/** Drop the thread and its marks / anchors in one rewrite. */
export function deleteComment(doc: string, id: string): string {
  const parsed = parseZwDocCached(doc);
  if (parsed.ok === false) return doc;
  const { [id]: _removed, ...comments } = parsed.value.header.comments;
  return serializeZwDoc({
    header: { ...parsed.value.header, comments },
    doc: withoutComment(parsed.value.doc, id),
  });
}

/** Drop threads whose marks / anchors are gone (header-only edit; on save). */
export function gcCommentStore(doc: string): string {
  const store = readCommentStore(doc);
  if (Object.keys(store).length === 0) return doc;
  const parsed = parseZwDocCached(doc);
  if (parsed.ok === false) return doc;
  const live = new Set(zwDocCommentRanges(parsed.value.doc).map((r) => r.id));
  const kept = Object.fromEntries(
    Object.entries(store).filter(([id]) => live.has(id)),
  );
  return Object.keys(kept).length === Object.keys(store).length
    ? doc
    : writeCommentStore(doc, kept);
}
